import React, { ChangeEvent, memo, useCallback, useMemo } from 'react'

import Svg from '../../../svg/svg'
import { ActionButton } from '../../..'
import { Col, Row } from 'react-grid-system'

import * as styles from './styles.module.scss'
import { Input } from '../../../form/fields'
import { WarehouseCardProps } from './types'

const WarehouseCard = ({
   warehouse,
   handleQuantityChange,
   handleReorderLevelChange,
   isNewItem,
   handleLoan,
   handleTransfer,
   handleAddToKit,
   handleAdjust,
   permissions,
   isCopyAction,
   isReadonly,
}: WarehouseCardProps) => {
   const {
      id,
      label: warehouseName,
      quantity,
      reorderLevel,
      isHomeWarehouse,
      kitQuantity,
   } = warehouse

   const cardTitle = useMemo(
      () => (isHomeWarehouse ? 'Home Warehouse' : null),
      [isHomeWarehouse]
   )

   const cardStyle = useMemo(
      () =>
         isHomeWarehouse
            ? {
                 paddingLeft: '0',
                 gap: '8px',
              }
            : {
                 paddingLeft: '8px',
                 gap: '0',
              },
      [isHomeWarehouse]
   )

   const svgId = useMemo(
      () => (isHomeWarehouse ? 'warehouse-yellow-bg' : 'locations'),
      [isHomeWarehouse]
   )

   const svgStroke = useMemo(
      () => (isHomeWarehouse ? null : 'black'),
      [isHomeWarehouse]
   )

   const svgViewbox = useMemo(
      () => (isHomeWarehouse ? null : '0 0 17 20'),
      [isHomeWarehouse]
   )

   const isQuantityEditable = useMemo(
      () => isCopyAction || isNewItem === true || warehouse?.toolWarehouseId === 0,
      [isNewItem, warehouse]
   )

   const onQuantityChange = useCallback(
      (e: ChangeEvent<HTMLFormElement>) =>
         handleQuantityChange(id, e.target.value),
      [handleQuantityChange, id]
   )

   const quantityInputProps = useMemo(
      () => ({
         type: 'number',
         min: 0,
      }),
      []
   )

   const quantityLabel = useMemo(() => `Quantity: ${quantity}`, [quantity])

   const kitQuantityLabel = useMemo(
      () => `+${kitQuantity} More in Kits`,
      [kitQuantity]
   )

   const onReorderLevelChange = useCallback(
      (e: ChangeEvent<HTMLFormElement>) =>
         handleReorderLevelChange(id, e.target.value),
      [handleReorderLevelChange, id]
   )

   const allowLoan = useMemo(
      () => !isQuantityEditable && quantity > 0,
      [isQuantityEditable, quantity]
   )

   const allowTransfer = useMemo(
      () =>
         permissions.includes('TransferTo') &&
         !isQuantityEditable &&
         quantity > 0,
      [permissions, isQuantityEditable, quantity]
   )

   const allowAddToKit = useMemo(
      () =>
         permissions.includes('AddtokitOpenKit') &&
         !isQuantityEditable &&
         quantity > 0,
      [permissions, isQuantityEditable, quantity]
   )

   const allowAdjust = useMemo(
      () => permissions.includes('Adjust') && !isQuantityEditable,
      [permissions, isQuantityEditable]
   )

   return (
      <article className={styles.card} aria-labelledby="warehouse-name">
         <header className={styles.header} title={cardTitle} style={cardStyle}>
            <Svg id={svgId} stroke={svgStroke} overrideViewBox={svgViewbox} />
            <h3 id="warehouse-name" className={styles.title}>
               {warehouseName}
            </h3>
         </header>

         <Row nogutter>
            <Col sm={8}>
               <section className={styles.content}>
                  {isQuantityEditable ? (
                     <Input
                        id={`${id}-quantity`}
                        label="Quantity"
                        onChange={onQuantityChange}
                        value={quantity > 0 ? quantity : '0'}
                        inputProps={quantityInputProps}
                        className={styles.quantityInput}
                        noMargin
                     />
                  ) : (
                     <div className={styles.quantities}>
                        <div aria-label={quantityLabel}>QTY: {quantity}</div>
                        {kitQuantity > 0 && (
                           <div
                              className={styles.kitQuantity}
                              aria-label={kitQuantityLabel}
                           >
                              {kitQuantityLabel}
                           </div>
                        )}
                     </div>
                  )}

                  {isHomeWarehouse && (
                     <Input
                        id={`${id}-reorder-level`}
                        label="Reorder Level"
                        onChange={onReorderLevelChange}
                        value={reorderLevel > 0 ? reorderLevel : '0'}
                        inputProps={quantityInputProps}
                        className={styles.reorder}
                        disabled={!isCopyAction && isReadonly}
                        noMargin
                     />
                  )}
               </section>
            </Col>
            <Col sm={4}>
               <footer
                  className={isReadonly ? styles.hiddenActions : styles.actions}
               >
                  {allowLoan && (
                     <ActionButton
                        title="Loan"
                        iconId="actionLoan"
                        action={() => handleLoan(id)}
                     />
                  )}
                  {allowTransfer && (
                     <ActionButton
                        title="Transfer"
                        iconId="actionTransfer"
                        action={() => handleTransfer(id)}
                     />
                  )}
                  {allowAdjust && (
                     <ActionButton
                        title="Adjust"
                        iconId="adjust"
                        action={() => handleAdjust(id)}
                     />
                  )}
                  {allowAddToKit && (
                     <ActionButton
                        title="Add To Kit"
                        iconId="add-to-kit"
                        action={() => handleAddToKit(id)}
                     />
                  )}
               </footer>
            </Col>
         </Row>
      </article>
   )
}

export default memo(WarehouseCard)
